import {SuiClient, SuiObjectResponse} from "@mysten/sui/client";
import {getObjectFields, getObjectType} from "./objects";
import {Position, Vault} from "../types";
import {MmtSDK} from "@mmt-finance/clmm-sdk";
import {asIntN, extractStructTagFromType} from "./utils";
import {handleMessageError, VaultsErrorCode} from "../errors";
import BN from "bn.js";
import {MathUtil} from "./math";
import {d, TickMath} from "./tickMath";
import Decimal from "./decimal";
import {ExtendedPoolWithApr} from "@mmt-finance/clmm-sdk/dist/types";

async function buildVaultInternal(fields: any, suiClient: SuiClient, type: string, pool: ExtendedPoolWithApr) {
  let is_lock = false;
  if (!Decimal(fields.lock_threshold_a).equals(0) || !Decimal(fields.lock_threshold_b).equals(0)) {
    is_lock = Decimal(fields.free_balance_a).greaterThanOrEqualTo(Decimal(fields.lock_threshold_a)) ||
      Decimal(fields.free_balance_b).greaterThanOrEqualTo(fields.lock_threshold_b)
  }

  let is_deposit_enabled = false;
  let liquidity = null;
  let position = null;
  const dynamicFieldPage = await suiClient.getDynamicFields({parentId: fields.id.id});
  const dynamicFieldMap: Record<string, string> = {};
  for (const data of dynamicFieldPage.data) {
    const value = data.name.value as Iterable<number>;
    const name = new TextDecoder().decode(new Uint8Array(value));
    if (name === 'deposit_enabled') {
      dynamicFieldMap['deposit_enabled'] = data.objectId as string;
    } else if (name === 'position') {
      dynamicFieldMap['position'] = data.objectId as string;
    }

  }
  const suiObjectResponses = await suiClient.multiGetObjects({
    ids: Object.values(dynamicFieldMap),
    options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
  });
  for (const suiObjectResponse of suiObjectResponses) {
    const suiObjectData = suiObjectResponse.data;
    if (!suiObjectData || !suiObjectData.content || !suiObjectData.content.dataType
      || suiObjectData.content.dataType !== 'moveObject') {
      continue;
    }
    if (dynamicFieldMap['deposit_enabled'] && dynamicFieldMap['deposit_enabled'] === suiObjectData.objectId) {
      const value = suiObjectData.content.fields as { value: boolean };
      is_deposit_enabled = value.value;
    } else if (dynamicFieldMap['position'] && dynamicFieldMap['position'] === suiObjectData.objectId) {
      const value = suiObjectData.content.fields as { value: { fields: { liquidity: string } } };
      liquidity = value.value.fields.liquidity;
      position = buildPosition(suiObjectData.content.fields, fields.decimals_a, fields.decimals_b)
    }
  }

  if (liquidity == null || position == null) {
    return handleMessageError(VaultsErrorCode.BuildError, "buildVault error, liquidity or position is null");
  }

  let suiStructTag = extractStructTagFromType(type);

  const price = TickMath.sqrtPriceX64ToPrice(new BN(pool.currentSqrtPrice), fields.decimals_a, fields.decimals_b);
  const upper_trigger_price_scalling = price.mul(MathUtil.u64ToRatio(fields.upper_price_scalling));
  const lower_trigger_price_scalling = price.mul(MathUtil.u64ToRatio(fields.lower_price_scalling));
  return {
    id: fields.id.id,
    clmm_pool_id: fields.clmm_pool_id,
    free_balance_a: fields.free_balance_a,
    free_balance_b: fields.free_balance_b,
    fee_a: fields.fee_a,
    fee_b: fields.fee_b,
    seed_balance: fields.seed_balance,
    upper_price_scalling: fields.upper_price_scalling,
    lower_price_scalling: fields.lower_price_scalling,
    upper_trigger_price_scalling_dec: upper_trigger_price_scalling,
    lower_trigger_price_scalling_dec: lower_trigger_price_scalling,
    upper_price_scalling_dec: MathUtil.u64ToRatio(fields.upper_price_scalling),
    lower_price_scalling_dec: MathUtil.u64ToRatio(fields.lower_price_scalling),
    lower_trigger_price: fields.config.fields.t.fields.lower_trigger_price,
    upper_trigger_price: fields.config.fields.t.fields.upper_trigger_price,
    last_rebalance_sqrt_price: fields.last_rebalance_sqrt_price,
    last_rebalance_sqrt_price_dec: MathUtil.u64ToRatio(fields.last_rebalance_sqrt_price),
    last_rebalance_time: fields.last_rebalance_time,
    deposit_limit: fields.deposit_limit,
    free_threshold_a: fields.free_threshold_a,
    free_threshold_b: fields.free_threshold_b,
    lock_threshold_a: fields.lock_threshold_a,
    lock_threshold_b: fields.lock_threshold_b,
    slippage_up: fields.slippage_up,
    slippage_down: fields.slippage_down,
    slippage_up_dec: MathUtil.u64ToRatio(fields.slippage_up),
    slippage_down_dec: MathUtil.u64ToRatio(fields.slippage_down),
    fee_val: fields.fee_val,
    withdraw_fee_val: fields.withdraw_fee_val,
    decimals_a: fields.decimals_a,
    decimals_b: fields.decimals_b,
    coin_type_a: suiStructTag.type_arguments[0],
    coin_type_b: suiStructTag.type_arguments[1],
    lp_token_type: suiStructTag.type_arguments[2],
    config_type: suiStructTag.type_arguments[3],
    is_lock: is_lock,
    is_deposit_enabled: is_deposit_enabled,
    pool_sqrt_price: pool.currentSqrtPrice,
    total_supply: fields.treasury_cap.fields.total_supply.fields.value,
    liquidity: liquidity,
    position: position,
    pool: pool,
  }
}

export async function buildVault(mmtClmmSDK: MmtSDK, suiClient: SuiClient, objects: SuiObjectResponse): Promise<Vault> {
  const fields = getObjectFields(objects)
  const type = getObjectType(objects) as string
  if (fields) {
    const clmm_pool_id = fields.clmm_pool_id;
    const pool = await mmtClmmSDK.Pool.getPool(clmm_pool_id);
    return await buildVaultInternal(fields, suiClient, type, pool);
  }
  return handleMessageError(VaultsErrorCode.BuildError, 'buildVault error')
}

export async function buildVaultByPoolMap(poolMap: Record<string, ExtendedPoolWithApr>,
                                 suiClient: SuiClient, objects: SuiObjectResponse): Promise<Vault> {
  const fields = getObjectFields(objects)
  const type = getObjectType(objects) as string
  if (fields) {
    const clmm_pool_id = fields.clmm_pool_id;
    const pool = poolMap[clmm_pool_id];
    return await buildVaultInternal(fields, suiClient, type, pool);
  }
  return handleMessageError(VaultsErrorCode.BuildError, 'buildVault error')
}

function buildPosition(fields: any, decimalA: number, decimalB: number): Position {
  const clmmFields = fields.value.fields;
  const lowerTick = asIntN(BigInt(clmmFields.tick_lower_index.fields.bits));
  const upperTick = asIntN(BigInt(clmmFields.tick_upper_index.fields.bits));
  return {
    id: fields.id.id,
    pool_id: clmmFields.pool_id,
    type_x: clmmFields.type_x.fields.name,
    type_y: clmmFields.type_y.fields.name,
    name: clmmFields.name,
    liquidity: clmmFields.liquidity,
    clmm_position_id: clmmFields.id.id,
    clmm_pool_id: clmmFields.pool,
    tick_lower_index: lowerTick,
    tick_upper_index: upperTick,
    price_lower_tick_dec: TickMath.tickIndexToPrice(lowerTick, decimalA, decimalB),
    price_upper_tick_dec: TickMath.tickIndexToPrice(upperTick, decimalA, decimalB),
  }
}

export function getLpAmountByLiquidity(vault: Vault, current_liquidity: string) {
  if (vault.total_supply === '0') {
    return '0'
  }
  return d(vault.total_supply).mul(current_liquidity).div(vault.liquidity).toFixed(0, Decimal.ROUND_DOWN).toString()
}
